import { Extension, getNodeType, isMarkActive, getAttributes, getNodeAttributes, getMarkType } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Mark } from 'prosemirror-model';
import { CellSelection } from '@_ueberdosis/prosemirror-tables';

import {
  selectionHasListNode,
  marksToListAttr,
  getListAttributeChanges,
  findNodeFromSelection,
  isTextStyleMark,
  findLargestChildMarkFontSize,
} from '../../common/TextBasedWidgets.helpers';
import { getClosestCell } from '../TableWidget.helpers';
import { getNearestMarksFromSelection, MarkStore } from './EmptyTextStyle.helpers';

interface EmptyTextStyleOptions {}
const EXTENSION_NAME = 'emptyTextStyle';
const LIST_NODE_OFFSET = 2;

// create a markstore instance for storing marks for cells
export const markStore = MarkStore.getInstance();

// This extension handles:
// When text is deleted or when selection is changed in a cell or in the text seleciton
// When selection is on a line without marks
// When lists are added / marks are added to text within a list
// TODO: this can probably be optimized a bit more and could perhaps split out list to a separate extension
export const EmptyTextStyle = Extension.create<EmptyTextStyleOptions>({
  name: EXTENSION_NAME,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(EXTENSION_NAME),
        appendTransaction: (trs, oldState, newState) => {
          if (trs.length !== 1) return;
          let newTr = newState.tr;

          const storedMarks = newState.storedMarks;
          const newTextContent = newState.doc.textContent;
          const oldTextContent = oldState.doc.textContent;
          const selection = newState.selection;
          const previousSelection = oldState.selection;
          const isCellSelection = selection instanceof CellSelection;
          const isPreviousCellSelection = previousSelection instanceof CellSelection;
          const cellNode = getClosestCell(selection);
          const previousCellNode = getClosestCell(previousSelection);
          const paragraphSchema = this.editor.schema.nodes.paragraph;
          const textStyleMarkType = getMarkType('textStyle', newState.schema);

          // Case 1: A new line in a list has been added
          // Apply stored marks from previous selection (stored by the listItem extension)
          const previousListAttrs = this.editor.storage.listItem;
          const newLineList = !!previousListAttrs;
          if (newLineList) {
            const { pos: listPos } = findNodeFromSelection(newState, 'listItem');
            if (typeof listPos === 'number') {
              const listNode = newState.doc.nodeAt(listPos);

              // if the new line has text get the marks from the text
              if (listNode && listNode.textContent) {
                const marks = getNearestMarksFromSelection(newState);
                const listAttrs = marksToListAttr(marks);
                newTr.setNodeMarkup(listPos, null, listAttrs);
                newTr = newTr.setStoredMarks(marks);
              } else {
                // for empty lines apply the marks from the previous line (stored in previousListAttrs)
                newTr.setNodeMarkup(listPos, null, previousListAttrs);
                const { pos, node, hasNode } = findNodeFromSelection(newState, 'paragraph');

                // update the paragraph node with the list font size
                if (hasNode) newTr.setNodeMarkup(pos, null, { ...node.attrs, fontSize: previousListAttrs.fontSize });
                if (storedMarks) newTr = newTr.setStoredMarks(storedMarks);
              }
            }

            // reset the previousListAttrs storage
            this.editor.storage.listItem = null;

            // li with multiple different marks
            if (!storedMarks) {
              const marks = getNearestMarksFromSelection(newState);
              newTr = newTr.setStoredMarks(marks);
            }

            return newTr;
          }

          // Case 2: text added or removed after a li node and the marks are different from li marks
          // -> update li node with text marks
          // e.g: set cursor after * change font size / toggle bold and type
          const nodeType = getNodeType('listItem', this.editor.schema);
          const hasListNode = selectionHasListNode(newState, nodeType);
          const textStyle = getAttributes(newState, 'textStyle');
          const hasAddedText = newTextContent.length > oldTextContent.length;
          const hasRemovedText = newTextContent.length < oldTextContent.length;

          // Text added after li
          if (hasAddedText && hasListNode && previousCellNode?.node?.textContent.length) {
            const { pos: listPos } = findNodeFromSelection(newState, 'listItem');
            const { pos: oldListPos } = findNodeFromSelection(oldState, 'listItem');

            // apply the marks to the li node before the text
            if (typeof listPos === 'number' && typeof oldListPos === 'number') {
              const oldListNode = oldState.doc.nodeAt(oldListPos);

              // if the old li node is empty -> use the marks from the current selection
              if (oldListNode && !oldListNode.textContent.length) {
                const marks = getNearestMarksFromSelection(newState);
                const listAttrs = marksToListAttr(marks);
                newTr.setNodeMarkup(listPos, null, listAttrs);
                return newTr;
              }

              // get the style from the current attributes & marks
              const listAttrs = {
                ...textStyle,
                bold: isMarkActive(newState, 'bold'),
                italic: isMarkActive(newState, 'italic'),
              };
              // only if its the position right after the li node
              const startPos = oldState.selection.$from.pos;
              if (startPos - LIST_NODE_OFFSET === listPos) newTr.setNodeMarkup(listPos, null, listAttrs);
            }

            return newTr;
          }
          // Text removed in front of li with different marks
          if (hasRemovedText && hasListNode && cellNode?.node?.textContent.length) {
            const newToPos = newState.selection.$to.pos;
            const { pos: listPos, node: listNode } = findNodeFromSelection(newState, 'listItem');

            if (typeof listPos === 'number' && newToPos - LIST_NODE_OFFSET === listPos) {
              // if the text is completely deleted on the li line -> use the old marks
              if (!listNode.textContent.length) {
                const oldMarks = getNearestMarksFromSelection(oldState);
                const listAttrs = marksToListAttr(oldMarks);
                newTr.setNodeMarkup(listPos, null, listAttrs);
                newTr = newTr.setStoredMarks(oldMarks);
                return newTr;
              }

              // if there is still text on the li line -> use the marks from the text on the li line closest to the li
              const newMarks = getNearestMarksFromSelection(newState);
              const listAttrs = marksToListAttr(newMarks);
              newTr.setNodeMarkup(listPos, null, listAttrs);
              newTr = newTr.setStoredMarks(newMarks);
              return newTr;
            }
          }

          // Case 3: Selection contains a li node and marks are updated
          // -> the li node should be updated with the new marks
          // e.g. select all in a list item and change font size
          // obs. partially applies list styling over selection - if text right after li node is selected
          const fromPos = newState.selection.$from.pos;
          const toPos = newState.selection.$to.pos;
          const oldFromPos = oldState.selection.$from.pos;
          const oldToPos = oldState.selection.$to.pos;
          const isSameSelection = fromPos === oldFromPos && toPos === oldToPos;

          if (isSameSelection && hasListNode) {
            // apply the marks to the li node before the text
            newState.selection.ranges.forEach((range) => {
              const from = range.$from.pos;
              const to = range.$to.pos;

              newState.doc.nodesBetween(from, to, (node, pos) => {
                if (nodeType && nodeType === node.type) {
                  // only list nodes within selection should be updated
                  const isLiNodeWithinSelection = pos + 2 >= from && pos <= to - 3;
                  if (isLiNodeWithinSelection) {
                    const hasTextContent = node.textContent.length > 0;

                    // if the li node has text content -> update the li node with the marks from the text
                    if (hasTextContent) {
                      let childMarks: Mark[] = [];
                      node.descendants((child) => {
                        if (child.isText && !childMarks.length) childMarks = child.marks as Mark[];
                      });
                      const listAttrs = marksToListAttr(childMarks);
                      newTr.setNodeMarkup(pos, null, listAttrs);
                      // else get the attribute that changed from old and new and apply it to the li node
                    } else {
                      const attributes = getListAttributeChanges(oldState, newState);
                      newTr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attributes });
                    }
                  }
                }
              });
            });
          }

          // Case 4: when editing text in a cell node and the text is cleared
          // -> store the marks from the previous state
          const textIsCompletelyDeleted =
            cellNode?.node?.textContent?.length === 0 && previousCellNode?.node?.textContent?.length! > 0;
          const isSameCell = cellNode?.pos === previousCellNode?.pos;
          const paragraphAttrs = getNodeAttributes(newState, 'paragraph');
          const oldParagraphAttr = getNodeAttributes(oldState, 'paragraph');

          // If the cellnode is changed, clear the stored marks
          if (!isSameCell) markStore.clearMarks();

          if (textIsCompletelyDeleted) {
            const { pos: listPos, hasNode: hasListNode } = findNodeFromSelection(newState, 'listItem');
            // Can be empty for cell selection -> use the marks from the previous state
            const prevMarkSelection = oldState.selection.$from.marks();
            let prevMarks = prevMarkSelection.length ? prevMarkSelection : getNearestMarksFromSelection(oldState);

            if (hasListNode) {
              const listAttrs = marksToListAttr(prevMarks);
              newTr.setNodeMarkup(listPos, null, listAttrs);
            }

            // store the marks from the pre-deleted state
            markStore.setMarks(prevMarks as Mark[], cellNode?.pos!);

            const { hasNode, node, pos } = findNodeFromSelection(newState, 'paragraph');
            let fontSize = hasNode ? node.attrs.fontSize : null;

            // TODO: this can be adjusted for cells with different font sizes
            // currently takes the first font size from the selection and applies it to all the empty cells
            if (isCellSelection) {
              selection.ranges.forEach((range) => {
                const from = range.$from.pos;
                const to = range.$to.pos;

                newState.doc.nodesBetween(from, to, (node, pos) => {
                  if (node.type.name === 'paragraph') {
                    newTr.setNodeMarkup(pos, null, { ...node.attrs, fontSize: oldParagraphAttr.fontSize });
                  }
                });
              });
            }

            // moving to a new empty cell shouldn't apply the marks from the previous cell
            if (isSameCell) {
              if (!fontSize && hasNode && !isCellSelection) {
                const fs = prevMarks.find((mark) => mark.type.name === 'textStyle')?.attrs?.fontSize;
                if (fs) newTr.setNodeMarkup(pos, null, { fontSize: fs });
              }

              newTr = newTr.setStoredMarks(prevMarks);
            } else {
              let newMarks = getNearestMarksFromSelection(newState);

              if (fontSize) {
                // replace font size in textStyle in prevMarks
                newMarks = newMarks.map((mark) => {
                  if (mark.type.name === 'textStyle') return mark.type.create({ ...mark.attrs, fontSize });
                  return mark;
                });
              }

              if (!fontSize && hasNode && !isCellSelection) {
                const fs = newMarks.find((mark) => mark.type.name === 'textStyle')?.attrs?.fontSize;
                if (fs) newTr.setNodeMarkup(pos, null, { fontSize: fs });
              }

              newTr = newTr.setStoredMarks(newMarks);
            }

            // store marks for the list item too if the text is completely deleted
            const listItemExt = this.editor.extensionManager.extensions.find((ext) => ext.name === 'listItem');
            if (listItemExt) listItemExt.options.marks = prevMarks;

            return newTr;
          }

          // should still be possible to add marks when the cell is empty
          if (cellNode?.node?.textContent.length === 0 && storedMarks?.length) {
            // should update empty list attributes too
            const { pos, node } = findNodeFromSelection(newState, 'listItem');
            if (node && node.textContent.length === 0) {
              const listAttrs = marksToListAttr(storedMarks);
              newTr.setNodeMarkup(pos, null, listAttrs);
            }

            markStore.setMarks(storedMarks as Mark[], cellNode?.pos!);
            storedMarks?.forEach((mark) => newTr.addStoredMark(mark));

            return newTr;
          }

          // Case 5 when the cursor is moved to a newline in a table data cell that is empty
          // -> grab the nearest marks and apply them
          // did the cursor change position
          const hasDocChanges = trs.some((transaction) => transaction.docChanged) && !oldState.doc.eq(newState.doc);
          const { empty, $anchor } = newState.selection;
          const { parent } = $anchor;
          const isEmptyLine = parent.textContent.length === 0;
          const nodeCount = newState.doc.nodeSize;
          const oldNodeCount = oldState.doc.nodeSize;
          const isSameNodeCount = nodeCount === oldNodeCount;
          const hasStoredMarks = !!storedMarks?.length;

          if (empty && !hasDocChanges && isEmptyLine && isSameNodeCount && !hasStoredMarks) {
            // grab the nearest marks and apply them
            const memoryStoredMarks = markStore.getMarks(cellNode?.pos);
            const marks = memoryStoredMarks?.length ? memoryStoredMarks : getNearestMarksFromSelection(newState);
            const textStyleAttrs = marks.find(isTextStyleMark)?.attrs || {};

            // font size should be from the paragraph on the empty line (not from the line above/below)
            const { hasNode, pos, node } = findNodeFromSelection(newState, 'paragraph');
            let fontSize = paragraphAttrs.fontSize;
            // if the paragraph doesn't have a font size, use the font size from the line above/below
            if (!fontSize) {
              fontSize = textStyleAttrs.fontSize;
              if (hasNode) newTr.setNodeMarkup(pos, null, { ...node.attrs, fontSize });
            }
            const newTextStyleMark = textStyleMarkType.create({ ...textStyleAttrs, fontSize });
            const newMarks = [...marks].filter((m) => !isTextStyleMark(m));
            newMarks.push(newTextStyleMark);

            newTr = newTr.setStoredMarks(newMarks);
            return newTr;
          }

          // Case 6: font size updated and text selection
          // -> update mark font size from the paragraph
          const isSameParagrapAttr = paragraphAttrs.fontSize === oldParagraphAttr.fontSize;
          const isSamePos = oldFromPos === fromPos;
          const hasNoTextStyle = !textStyle.fontFamily && !textStyle.fontSize && !textStyle.color;

          if (isSamePos && !hasStoredMarks && !isSameParagrapAttr && isSameNodeCount && !isEmptyLine) {
            newTr.addStoredMark(textStyleMarkType.create({ ...textStyle, fontSize: paragraphAttrs.fontSize }));
            return newTr;
          }

          // Case 7: A new empty line is added
          // -> Set the marks from the previous line
          // -> update the empty line paragraph font size
          if (nodeCount > oldNodeCount && isEmptyLine) {
            const { pos, node, hasNode } = findNodeFromSelection(newState, 'paragraph');

            if (isPreviousCellSelection && hasNoTextStyle) {
              const memoryStoredMarks = markStore.getMarks(cellNode?.pos);
              const marks = memoryStoredMarks?.length ? memoryStoredMarks : getNearestMarksFromSelection(newState);

              // empty cell selected -> press enter
              const newTextStyle = getAttributes(newState, 'textStyle');
              const hasNoNewTextStyle = !newTextStyle.fontFamily && !newTextStyle.fontSize && !newTextStyle.color;
              if (isPreviousCellSelection && hasNoNewTextStyle) {
                const { pos, node, hasNode } = findNodeFromSelection(newState, 'paragraph');
                const textStyleAttrs = marks.find(isTextStyleMark)?.attrs || {};
                const fontSize = textStyleAttrs.fontSize;
                if (hasNode) newTr.setNodeMarkup(pos, null, { ...node.attrs, fontSize });
              }

              newTr = newTr.setStoredMarks(marks);
              return newTr;
            }

            const textStyle = this.editor?.getAttributes('textStyle');

            // add the textStyle mark (always present)
            const newMarks: Mark[] = [textStyleMarkType.create({ ...textStyle })];

            // add the bold / italic / underline marks (if applied on the previous line)
            if (!!this.editor.isActive('bold')) newMarks.push(getMarkType('bold', newState.schema).create());
            if (!!this.editor.isActive('italic')) newMarks.push(getMarkType('italic', newState.schema).create());
            if (!!this.editor.isActive('underline')) newMarks.push(getMarkType('underline', newState.schema).create());

            // add font size to the paragraph
            const fontSize = textStyle?.fontSize;
            if (hasNode) newTr.setNodeMarkup(pos, paragraphSchema, { ...node.attrs, fontSize });

            newTr = newTr.setStoredMarks(newMarks);
            return newTr;
          }

          // case 8: Marks/attrs changed on a empty list line
          // -> update the list item with the new marks/attrs
          const isEmptyTextStyle = !textStyle.fontSize;
          if (isSameSelection && hasListNode && isEmptyLine) {
            const { pos, hasNode, node } = findNodeFromSelection(newState, 'listItem');
            const isEmptyListLine = hasNode && node.textContent.length === 0;

            if (hasNode && isEmptyListLine) {
              // if the text style is missing - grab the nearest marks from line above/below
              if (isEmptyTextStyle) {
                let marks: Mark[] = [...getNearestMarksFromSelection(newState)];
                const { node: paragraphNode } = findNodeFromSelection(newState, 'paragraph');

                // if the paragraph at the same empty li has a font size - use it & replace the list item font size
                const fontSize = paragraphNode?.attrs?.fontSize;
                const textStyleAttrs = marks.find(isTextStyleMark)?.attrs || {};
                const newTextStyleMark = textStyleMarkType.create({ ...textStyleAttrs, fontSize });

                // remove the old text style mark and add the new one
                marks = marks.filter((m) => !isTextStyleMark(m));
                marks.push(newTextStyleMark);

                const listAttrs = marksToListAttr(marks);
                newTr.setNodeMarkup(pos, this.editor.schema.nodes['listItem'], listAttrs);
                newTr = newTr.setStoredMarks(marks);

                return newTr;
              }

              // else use the stored marks
              const storedMarks = newTr.storedMarks;
              const bold = !!storedMarks?.find((mark) => mark.type.name === 'bold');
              const italic = !!storedMarks?.find((mark) => mark.type.name === 'italic');
              newTr.setNodeMarkup(pos, this.editor.schema.nodes['listItem'], { bold, italic, ...textStyle });
              newTr = newTr.setStoredMarks(storedMarks);
              return newTr;
            }
          }

          // sync the font size of the paragraph when text is deleted
          // text can have multiple marks, so we need to find the new largest font size in case the current largest font size is removed
          const isPaste = trs[0].getMeta('paste');
          if ((hasRemovedText || isPaste) && !isEmptyTextStyle) {
            const { node, pos, hasNode } = findNodeFromSelection(newState, 'paragraph');
            if (hasNode) {
              const storedMarks = newTr.storedMarks;
              const fontSize = findLargestChildMarkFontSize(node) + 'px';
              newTr.setNodeMarkup(pos, paragraphSchema, { ...node.attrs, fontSize });

              // check if the node has a empty line break
              let hasHardBreak = false;
              node?.content.forEach((child) => {
                if (child?.type.name === 'hardBreak') hasHardBreak = true;
              });
              // for empty lines
              if (node.textContent.length === 0 || hasHardBreak) newTr = newTr.setStoredMarks(storedMarks);

              return newTr;
            }
          }

          // cell selection fallback
          if (hasNoTextStyle && isPreviousCellSelection && !isCellSelection) {
            const memoryStoredMarks = markStore.getMarks(cellNode?.pos);
            const $node = newState.selection.$from;

            if (memoryStoredMarks && memoryStoredMarks.length) {
              const textStyleMark = memoryStoredMarks.find(isTextStyleMark);
              const fontSize = textStyleMark?.attrs?.fontSize;
              const { pos, hasNode, node } = findNodeFromSelection(newState, 'paragraph');
              if (hasNode) newTr.setNodeMarkup(pos, paragraphSchema, { ...node.attrs, fontSize });

              memoryStoredMarks.forEach((mark: Mark) => newTr.addMark($node.pos - 1, $node.pos + 1, mark));
              markStore.clearMarks();
              return newTr;
            }

            // cell selected -> type text
            // should apply the marks from the cell that was selected
            const oldTextStyle = getAttributes(oldState, 'textStyle');
            const hasOldTextStyle = !!oldTextStyle.fontSize;
            if (hasOldTextStyle) {
              const newMarks: Mark[] = [textStyleMarkType.create({ ...oldTextStyle })];

              if (!!this.editor.isActive('bold')) newMarks.push(getMarkType('bold', newState.schema).create());
              if (!!this.editor.isActive('italic')) newMarks.push(getMarkType('italic', newState.schema).create());
              if (!!this.editor.isActive('underline')) {
                newMarks.push(getMarkType('underline', newState.schema).create());
              }

              const { pos, hasNode, node } = findNodeFromSelection(newState, 'paragraph');
              if (hasNode)
                newTr.setNodeMarkup(pos, paragraphSchema, { ...node.attrs, fontSize: oldTextStyle.fontSize });

              newMarks.forEach((mark: Mark) => newTr.addMark($node.pos - 1, $node.pos + 1, mark));
              return newTr;
            }

            if (!hasOldTextStyle) {
              let marks: Mark[] = [...getNearestMarksFromSelection(newState)];
              const { pos, hasNode, node } = findNodeFromSelection(newState, 'paragraph');
              const fontSize = marks.find(isTextStyleMark)?.attrs?.fontSize;
              if (hasNode) newTr.setNodeMarkup(pos, paragraphSchema, { ...node.attrs, fontSize });
              newTr = newTr.setStoredMarks(marks);
            }

            return newTr;
          }

          // A general fallback for empty text style
          if (isEmptyTextStyle) {
            const { node, pos, hasNode } = findNodeFromSelection(newState, 'paragraph');
            let marks: Mark[] = [...getNearestMarksFromSelection(newState)];

            // edge case when adding a new empty line as the first line -> deletion removes node attributes
            if (isSamePos && fromPos === 1 && node) newTr.setNodeMarkup(pos, paragraphSchema, oldParagraphAttr);

            // update the paragraph font size from the nearest marks
            if (isEmptyLine && hasNode) {
              // use the existing paragraph font size if it exists
              const fontSize = node.attrs.fontSize || marks.find(isTextStyleMark)?.attrs.fontSize;
              newTr.setNodeMarkup(pos, paragraphSchema, { ...node.attrs, fontSize });

              const textStyleAttrs = marks.find(isTextStyleMark)?.attrs || {};

              // font size should be from the paragraph on the empty line (not from the line above/below)
              const newTextStyleMark = textStyleMarkType.create({ ...textStyleAttrs, fontSize });
              marks = marks.filter((m) => !isTextStyleMark(m));
              marks.push(newTextStyleMark);
            }

            newTr = newTr.setStoredMarks(marks);
            return newTr;
          }

          return newTr;
        },
      }),
    ];
  },
});
